import { Component, Input, Output, ContentChildren, EventEmitter, ViewChild, TemplateRef, AfterContentInit, QueryList, OnInit, OnDestroy, ContentChild, forwardRef, AfterViewInit, ChangeDetectorRef, AfterContentChecked, Directive } from '@angular/core';
import { ThemeManagerService } from 'src/app/services/theme-manager.service';
import { DialogComponent } from './dialog.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subscription, Observable, Subject } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { ConfirmationComponent } from './confirmation.component';

@Directive()
export abstract class WizardScreenComponent implements OnInit, OnDestroy {
  @Input() parentEvents: Observable<any>;
  private _readOnly: boolean;
  @Input()
  get readOnly(): boolean{
    return this._readOnly;
  }
  set readOnly(val: boolean) {
    this._readOnly = val;
    if(val) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  @Output() eventHandler: EventEmitter<any> = new EventEmitter();
  menuItem: WizardMenuItemComponent;
  private parentSubscription: Subscription;
  protected preventClose = false;
  public form: FormGroup;

  ngOnInit() {
    this.parentSubscription = this.parentEvents.subscribe((event) => { this.parentEvent(event)});
  }

  ngOnDestroy() {
    this.parentSubscription.unsubscribe();
  }

  parentEvent(event) {
    if(event.screen == this.menuItem.description) {
      switch(event.subject) {
        case 'LOAD':
          this.onLoad();
          break;
        case 'SAVE':
          this.onSave(event.isNext);
          break;
        case 'CANCEL':
          this.onCancel();
          break;
        case 'CLOSE':
          this.onClose();
          break;
      }
    }
  }

  showSpinner(message?: string) {
    this.eventHandler.emit({ state: 'SHOWSPINNER', message });
  }

  hideSpinner(message?: string) {
    this.eventHandler.emit({ state: 'HIDESPINNER' });
  }

  showPrompt(message: string, callback: (result: any) => void, buttons?: {label:string, value:any, color:any}[], hideCancel: boolean = false, title?: string) {
    this.eventHandler.emit({ state: 'SHOWPROMPT', message, callback, buttons, hideCancel, title })
  }

  showMessage(message: string, timeout?: number) {
    this.eventHandler.emit({ state: 'SHOWMESSAGE', message , timeout });
  }

  onLoad(){
    this.eventHandler.emit({ state: 'LOADCOMPLETE' });
  }

  onSave(isNext: boolean = true) {
    this.form.markAsPristine();
    this.eventHandler.emit({ state: 'SAVECOMPLETE', preventClose: this.preventClose, isNext });
  }

  preClose() {
    console.log('WizardScreenComponent.preClose()')
  }

  private closeAndConfirm(forceClose:boolean = true) {
    if(this.isDirty()) {
      this.eventHandler.emit({ state: 'CLOSECONFIRMATION', callback: shouldContinue => {
        if (shouldContinue) {
          this.preClose();
          if (forceClose) {
            this.eventHandler.emit({ state: 'FORCECLOSE'});
          }
        }
      }});
    } else {
      this.preClose();
      if (forceClose) {
        this.eventHandler.emit({ state: 'FORCECLOSE' });
      }
    }
  }

  onCancel(forceClose:boolean = true) {
    this.closeAndConfirm(forceClose);
  }

  onClose(forceClose:boolean = true) {
    this.closeAndConfirm(forceClose);
  }

  notifyWizardComponent(eventType, item?: any) {
    this.eventHandler.emit({ state: eventType, item});
  }

  isDirty(): boolean {
    return this.form.dirty;
  }

  isValid(): boolean {
    return this.form.valid;
  }

  isPreviousButtonDisabled(): boolean {
    return this.isSaveButtonDisabled();
  }

  isSaveButtonDisabled(): boolean {
    return !(this.isDirty() && this.isValid())
  }
}

@Component({
  selector: 'cw-wizard-menu-item',
  template: `
  <ng-template #customHeaderAction><ng-content select="div.custom-header-action"></ng-content></ng-template>
  <ng-template #content><ng-content></ng-content></ng-template>`
})
export class WizardMenuItemComponent implements AfterContentInit {
  @Input() isDefault = false;
  @Input() description;
  @Input() title;
  subTitle;
  @Input() svgIcon;
  @Input() matIcon;
  @Input() matFontSet;
  @Input() matStyle;
  @Input() hide = false;
  @ViewChild('content', {static:true}) public content: TemplateRef<any>;
  @ViewChild('customHeaderAction',{static:true}) public customHeaderAction:TemplateRef<any>;
  @ContentChild(WizardScreenComponent, {static: true}) component: WizardScreenComponent;
  public selected:boolean;
  parentMenu: WizardMenuSubmenuComponent;

  ngAfterContentInit() {
    this.component.menuItem = this;
  }
}

@Component({
  selector: 'cw-wizard-menu-divider',
  template: '',
  providers: [{provide: WizardMenuItemComponent, useExisting: forwardRef(() => WizardMenuDividerComponent)}]
})
export class WizardMenuDividerComponent extends WizardMenuItemComponent implements AfterContentInit {
  ngAfterContentInit(){};
}

@Component({
  selector: 'cw-wizard-menu-submenu',
  template: '',
  providers: [{provide: WizardMenuItemComponent, useExisting: forwardRef(() => WizardMenuSubmenuComponent)}]
})
export class WizardMenuSubmenuComponent extends WizardMenuItemComponent implements AfterContentInit {
  @ContentChildren(WizardMenuItemComponent) private _menuItems: QueryList<WizardMenuItemComponent>;
  @Input() autoAdvance: boolean;

  get menuItems(): WizardMenuItemComponent[] {
    return this._menuItems.filter(item => item !== this)
  };

  ngAfterContentInit(){
    this.menuItems.forEach(item => {
      item.parentMenu = this;
    });
  };
}

@Component({
  selector: 'cw-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss']
})
export class WizardComponent implements AfterContentInit {
  @ViewChild('dialog', { static:true }) dialog: DialogComponent;
  @ContentChildren(WizardMenuItemComponent) menuItems: QueryList<WizardMenuItemComponent>;
  @Input() autoAdvance = true;
  @Input() wizardTitle: string;
  @Output() eventHandler: EventEmitter<any> = new EventEmitter();
  public title : string;
  public subTitle : string;
  public displayButtons = true;
  public customHeaderAction: TemplateRef<any>;
  public content: TemplateRef<any>;
  public currentScreen: WizardMenuItemComponent;
  public nextButtonText: string;
  private originalAutoAdvance: boolean;

  constructor(public themeManager: ThemeManagerService, public matDialog: MatDialog) {
  }

  ngAfterContentInit() {
    let startingMenuItem = this.menuItems.first;

    const foundItem = this.menuItems.find(el => el.isDefault);
    if (foundItem) {
      startingMenuItem = foundItem;
    }

    this.menuClick(startingMenuItem);
  }

  public onEvent(event) {
    this.eventHandler.emit(event);
  }

  menuClick(menuItem : WizardMenuItemComponent) {
    if(this.currentScreen && this.currentScreen.component.isDirty()) {
      this.showUnsavedDataPrompt(shouldContinue => {
        if (shouldContinue) {
          this.currentScreen.component.preClose();
          this.eventHandler.emit({ state: 'MENUCLICK', menuItem});
        }
      });
    } else {
      if (this.currentScreen) {
        this.currentScreen.component.preClose();
      }
      this.eventHandler.emit({ state: 'MENUCLICK', menuItem });
    }
  }

  showSpinner(message?: string) {
    this.dialog.spinner.showSpinner(message);
  }

  hideSpinner() {
    this.dialog.spinner.hideSpinner();
  }

  showMessage(message: string, timeout: number) {
    this.dialog.spinner.showMessage(message, timeout);
  }

  showPrompt(message: string, callback: (result: any) => void, buttons?: {label:string, value:any, color:any}[], hideCancel: boolean = false, title?: string) {
    const dialogRef = this.matDialog.open(ConfirmationComponent, {
      panelClass: 'flush-dialog',
      data: {
        promptMessage: [
          message
        ],
        closeLabel: 'Cancel',
        buttons: buttons ? buttons : [{label:'Ok', value:true, color:'primary'}],
        height: 'auto',
        showClose: !hideCancel,
        promptTitle: title
      }
    });
    dialogRef.afterClosed().subscribe(shouldContinue => {
      callback(shouldContinue);
    });
  }

  hasNextScreen(): boolean {
    let hasNext = false;
    const menuItemsArray = this.menuItems.toArray();
    for(let i = 0; i < this.menuItems.length; i++) {
      if(menuItemsArray[i].description == this.currentScreen.description && i+1 < this.menuItems.length) {
        hasNext = true;
        break;
      }
      if(menuItemsArray[i] instanceof WizardMenuSubmenuComponent) {
        const submenu = menuItemsArray[i] as WizardMenuSubmenuComponent;
        for(let j = 0; j < submenu.menuItems.length; j++) {
          if(submenu.menuItems[j].description == this.currentScreen.description && j+1 < submenu.menuItems.length) {
            hasNext = true;
            return hasNext;
          }
        }
      }
    }
    return hasNext;
  }

  hasPrevScreen(): boolean {
    let hasPrev = false;
    const menuItemsArray = this.menuItems.toArray();
    for(let i = 0; i < this.menuItems.length; i++) {
      if(menuItemsArray[i].description == this.currentScreen.description && i-1 >= 0) {
        hasPrev = true;
        break;
      }
      if(menuItemsArray[i] instanceof WizardMenuSubmenuComponent) {
        const submenu = menuItemsArray[i] as WizardMenuSubmenuComponent;
        for(let j = 0; j < submenu.menuItems.length; j++) {
          if(submenu.menuItems[j].description == this.currentScreen.description && j-1 >= 0) {
            hasPrev = true;
            return hasPrev;
          }
        }
      }
    }
    return hasPrev;
  }

  next() {
    const menuItemsArray = this.menuItems.toArray();
    for(let i = 0; i < this.menuItems.length; i++) {
      if(menuItemsArray[i] instanceof WizardMenuSubmenuComponent) {
        const submenu = menuItemsArray[i] as WizardMenuSubmenuComponent;
        for(let j = 0; j < submenu.menuItems.length; j++) {
          if(submenu.menuItems[j].description == this.currentScreen.description) {
            this.menuClick(submenu.menuItems[j+1]);
            break;
          }
        }
      }
      if(menuItemsArray[i].description == this.currentScreen.description) {
        this.menuClick(menuItemsArray[i+1]);
        break;
      }
    }
  }

  prev() {
    const menuItemsArray = this.menuItems.toArray();
    for(let i = 0; i < this.menuItems.length; i++) {
      if(menuItemsArray[i] instanceof WizardMenuSubmenuComponent) {
        const submenu = menuItemsArray[i] as WizardMenuSubmenuComponent;
        for(let j = 0; j < submenu.menuItems.length; j++) {
          if(submenu.menuItems[j].description == this.currentScreen.description) {
            this.menuClick(submenu.menuItems[j-1]);
            this.updateNextButtonText()
            break;
          }
        }
      }
      if(menuItemsArray[i].description == this.currentScreen.description) {
        this.menuClick(menuItemsArray[i-1]);
        this.updateNextButtonText()
        break;
      }
    }
  }

  updateNextButtonText(text?: string) {
    if(text) {
      this.nextButtonText = text;
    } else if(this.autoAdvance && this.hasNextScreen()){
      this.nextButtonText = 'Next';
    } else {
      this.nextButtonText = 'Save';
    }
  }

  displayScreen(newScreen: WizardMenuItemComponent) {
    if(this.currentScreen) {
      this.currentScreen.selected = false;
      if(this.currentScreen.parentMenu) {
        this.currentScreen.parentMenu.selected = false;
        if(this.currentScreen.parentMenu.autoAdvance != undefined) {
          this.autoAdvance = this.originalAutoAdvance;
        }
      }
      this.hideSpinner();
    }
    if(newScreen instanceof WizardMenuSubmenuComponent) {
      newScreen = (newScreen.menuItems[0] as WizardMenuSubmenuComponent);
    }
    this.currentScreen = newScreen;
    this.currentScreen.selected = true;
    if(this.currentScreen.parentMenu) {
      this.currentScreen.parentMenu.selected = true;
      if(this.currentScreen.parentMenu.autoAdvance != undefined) {
        this.originalAutoAdvance = this.autoAdvance;
        this.autoAdvance = this.currentScreen.parentMenu.autoAdvance;
      }
    }
    this.content = newScreen.content;
    if (newScreen.title) {
      this.title = newScreen.title;
      this.subTitle = newScreen.description;
    } else {
      this.title = newScreen.description;
      this.subTitle = undefined;
    }
    this.customHeaderAction = newScreen.customHeaderAction;
    this.updateNextButtonText()
    this.eventHandler.emit({ state: 'LOAD' });
  }

  getCurrentScreen(): string {
    return this.currentScreen.description;
  }

  showUnsavedDataPrompt(callback: (result: any) => void) {
    this.showPrompt('There are unsaved changes, are you sure you want to leave this page?', callback,
      [{label: 'Continue', value:true, color:'primary'}]);
  }

  isDivider(menuItem) {
    return menuItem instanceof WizardMenuDividerComponent;
  }

  isSubmenu(menuItem) {
    return menuItem instanceof WizardMenuSubmenuComponent;
  }
}

@Directive()
export abstract class WizardManager implements OnInit {
  @ViewChild('wizard', { static: true })  wizard: WizardComponent;
  public wizardSubject: Subject<any> = new Subject<string>();

  constructor(private dialogRef: MatDialogRef<WizardManager>) {
    dialogRef.backdropClick().subscribe(event => {
      this.onEvent({state: 'CLOSECLICKED'})
    });
  }

  ngOnInit() {
  }

  public onEvent(event) {
    switch(event.state) {
      case 'SHOWSPINNER':
        if(event.message) {
          this.wizard.showSpinner(event.message);
        }
        else {
          this.wizard.showSpinner();
        }
        break;
      case 'HIDESPINNER':
        this.wizard.hideSpinner();
        break;
      case 'SHOWMESSAGE':
        this.wizard.showMessage(event.message, event.timeout ? event.timeout : 3500);
        break;
      case 'SHOWPROMPT' :
        this.wizard.showPrompt(event.message, event.callback, event.buttons, event.hideCancel, event.title);
        break;
      case 'CLOSECLICKED':
        this.onCloseClicked();
        break;
      case 'CANCEL':
        this.onCancel();
        break;
      case 'FORCECLOSE':
        this.onClose();
        break;
      case 'LOAD':
        this.onLoad();
        break;
      case 'PREVIOUS':
        this.onSave(false);
        break;
      case 'SAVE':
        this.onSave(true);
        break;
      case 'SAVECOMPLETE':
        if(this.wizard.autoAdvance) {
          if(event.isNext && this.wizard.hasNextScreen()){
            this.wizard.next();
          } else if(!event.isNext && this.wizard.hasPrevScreen()) {
            this.wizard.prev();
          } else {
            if(!event.preventClose){
              this.onClose();
            }
          }
        }
        break;
      case 'MENUCLICK':
        this.onMenuClick(event);
        break;
      case 'CLOSECONFIRMATION':
        this.wizard.showUnsavedDataPrompt(event.callback);
        break;
    }
  }

  protected onCloseClicked() {
    const screenKey = this.wizard.getCurrentScreen();
    this.wizardSubject.next({ subject: 'CLOSE', screen: screenKey });
  }

  protected onCancel() {
    const screenKey = this.wizard.getCurrentScreen();
    this.wizardSubject.next({ subject: 'CANCEL', screen: screenKey });
  }

  protected onClose() {
    this.dialogRef.close();
  }

  protected onLoad() {
    const screenKey = this.wizard.getCurrentScreen();
    this.wizardSubject.next({ subject: 'LOAD', screen: screenKey });
  }

  protected onSave(isNext: boolean) {
    const screenKey = this.wizard.getCurrentScreen();
    this.wizardSubject.next({ subject: 'SAVE', screen: screenKey, isNext });
  }

  protected onMenuClick(event) {
    this.wizard.displayScreen(event.menuItem);
  }
}